// User Onboarding Flow Component
import React, { useState, useCallback , useMemo } from 'react';
import { User, ResumeData } from '../types';
import { JobTitleStep } from './onboarding/JobTitleStep';
import { ResumeUploadStep } from './onboarding/ResumeUploadStep';
import { JobDescriptionStep } from './onboarding/JobDescriptionStep';
import { SystemPromptStep } from './onboarding/SystemPromptStep';
import { ReviewStep } from './onboarding/ReviewStep';
import { PythonBackendService } from '../services/pythonBackendService';
import { ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';

interface OnboardingFlowProps {
  onComplete: (userData: Partial<User>) => void;
  onCancel: () => void;
}

type OnboardingStep = 'job-title' | 'resume' | 'job-description' | 'system-prompt' | 'review';

interface OnboardingData {
  jobTitle: string;
  resumeFile: File | null;
  resumeData: ResumeData | null;
  jobDescription: string;
  systemPrompt: string;
}

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({
  onComplete,
  onCancel
}) => {
  const [currentStep, setCurrentStep] = useState<OnboardingStep>('job-title');
  const [data, setData] = useState<OnboardingData>({
    jobTitle: '',
    resumeFile: null,
    resumeData: null,
    jobDescription: '',
    systemPrompt: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const steps = useMemo(
    () => [
      {
        key: 'job-title',
        title: 'Job Title',
        description: 'What position are you interviewing for?'
      },
      {
        key: 'resume',
        title: 'Resume Upload',
        description: 'Upload your resume for AI analysis'
      },
      {
        key: 'job-description',
        title: 'Job Description',
        description: 'Paste the job description'
      },
      {
        key: 'system-prompt',
        title: 'AI Personalization',
        description: 'Customize how the AI should respond'
      },
      {
        key: 'review',
        title: 'Review',
        description: 'Review your information'
      }
    ] as const,
    [] // no dependencies, since it's static
  );
  

  const currentStepIndex = steps.findIndex(step => step.key === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  const updateData = useCallback((updates: Partial<OnboardingData>) => {
    setData(prev => ({ ...prev, ...updates }));
  }, []);

  const goToNextStep = useCallback(() => {
    if (isLastStep) return;
    const nextStepIndex = currentStepIndex + 1;
    setCurrentStep(steps[nextStepIndex].key);
  }, [currentStepIndex, isLastStep, steps]);
  
  const goToPreviousStep = useCallback(() => {
    if (isFirstStep) return;
    const prevStepIndex = currentStepIndex - 1;
    setCurrentStep(steps[prevStepIndex].key);
  }, [currentStepIndex, isFirstStep, steps]);
  

  const canProceedToNext = useCallback((): boolean => {
    switch (currentStep) {
      case 'job-title':
        return data.jobTitle.trim().length > 0;
      case 'resume':
        return data.resumeFile !== null && data.resumeData !== null;
      case 'job-description':
        return data.jobDescription.trim().length > 0;
      case 'system-prompt':
        return data.systemPrompt.trim().length > 0;
      case 'review':
        return true;
      default:
        return false;
    }
  }, [currentStep, data]);

  const handleComplete = useCallback(async () => {
    if (!canProceedToNext()) return;

    setIsLoading(true);
    
    try {
      // Create backend service instance
      const backendService = new PythonBackendService();
      
      // Generate a temporary user ID for preferences
      const tempUserId = `user_${Date.now()}`;
      
      // Generate unified session ID (will be used for all purposes)
      const unifiedSessionId = `session_${Date.now()}`;
      console.log(`🆔 Generated unified session ID: ${unifiedSessionId}`);
      
      // Convert resume file to base64 if available
      let resumeFileBase64 = '';
      let resumeFileType = '';
      if (data.resumeFile) {
        resumeFileBase64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Remove the data URL prefix (data:application/pdf;base64,)
            const [meta,base64] = result.split(',');
            resumeFileType = meta.split(':')[1].split(';')[0];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(data.resumeFile!);
        });
      }
      
      // Send user preferences to backend with unified session ID
      console.log('📤 Sending user preferences to backend during onboarding...');
      await backendService.setUserPreferences({
        userId: tempUserId,
        jobTitle: data.jobTitle,
        jobDescription: data.jobDescription,
        systemPrompt: data.systemPrompt,
        resumeFile: resumeFileBase64,
        resumeFileType: resumeFileType,
        manualTriggerEnabled: false, // Default to automatic mode
        sessionId: unifiedSessionId // Pass unified session ID
      });
      console.log('✅ User preferences sent to backend successfully');
      
      // Create user data for frontend
      const userData: Partial<User> = {
        id: tempUserId, // Use the same ID that was sent to backend
        jobTitle: data.jobTitle,
        resumeData: data.resumeData!,
        jobDescription: data.jobDescription,
        systemPrompt: data.systemPrompt,
        sessionId: unifiedSessionId, // Pass unified session ID
        createdAt: new Date()
      };
      
      console.log(`🔍 OnboardingFlow: Using user ID: ${tempUserId}`);

      onComplete(userData);
    } catch (error) {
      console.error('Error completing onboarding:', error);
      alert('Failed to save your preferences. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [data, canProceedToNext, onComplete]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 'job-title':
        return (
          <JobTitleStep
            jobTitle={data.jobTitle}
            onJobTitleChange={(jobTitle) => updateData({ jobTitle })}
          />
        );
      case 'resume':
        return (
          <ResumeUploadStep
            resumeFile={data.resumeFile}
            resumeData={data.resumeData}
            onResumeUpload={(file, extractedData) => 
              updateData({ resumeFile: file, resumeData: extractedData })
            }
          />
        );
      case 'job-description':
        return (
          <JobDescriptionStep
            jobDescription={data.jobDescription}
            onJobDescriptionChange={(jobDescription) => updateData({ jobDescription })}
          />
        );
      case 'system-prompt':
        return (
          <SystemPromptStep
            systemPrompt={data.systemPrompt}
            resumeData={data.resumeData}
            jobDescription={data.jobDescription}
            onSystemPromptChange={(systemPrompt) => updateData({ systemPrompt })}
          />
        );
      case 'review':
        return (
          <ReviewStep
            data={data}
            onEdit={(step) => setCurrentStep(step)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Interview CoPilot Setup
          </h1>
          <p className="text-gray-600">
            Let's configure your AI interview assistant in just a few steps
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between relative">
            {/* Progress line background */}
            <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 -z-10" style={{ 
              left: '2.5rem', 
              right: '2.5rem' 
            }} />
            
            {steps.map((step, index) => {
              const isActive = step.key === currentStep;
              const isCompleted = index < currentStepIndex;
              
              return (
                <div key={step.key} className="flex flex-col items-center flex-1 relative">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium relative z-10
                    ${isCompleted 
                      ? 'bg-success-600 text-white' 
                      : isActive 
                        ? 'bg-primary-600 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }
                  `}>
                    {isCompleted ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    <div className={`text-sm font-medium ${
                      isActive ? 'text-primary-600' : 'text-gray-600'
                    }`}>
                      {step.title}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {step.description}
                    </div>
                  </div>
                  
                  {/* Progress line segment */}
                  {index < steps.length - 1 && (
                    <div className={`
                      absolute top-5 h-0.5 -z-10
                      ${isCompleted ? 'bg-success-600' : 'bg-gray-200'}
                    `} style={{ 
                      left: 'calc(50% + 1.25rem)', 
                      right: 'calc(-50% + 1.25rem)',
                      width: 'calc(100% - 2.5rem)'
                    }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="card animate-fadeInUp">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-8">
          <button
            onClick={isFirstStep ? onCancel : goToPreviousStep}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {isFirstStep ? 'Cancel' : 'Previous'}
          </button>

          <div className="flex space-x-3">
            {!isLastStep ? (
              <button
                onClick={goToNextStep}
                disabled={!canProceedToNext()}
                className={`
                  flex items-center px-6 py-2 rounded-lg font-medium transition-colors
                  ${canProceedToNext()
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }
                `}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={!canProceedToNext() || isLoading}
                className={`
                  flex items-center px-6 py-2 rounded-lg font-medium transition-colors
                  ${canProceedToNext() && !isLoading
                    ? 'bg-success-600 text-white hover:bg-success-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }
                `}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                    Setting Up...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete Setup
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
